import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { RegionService } from '../../shared/services/region.service';
import { BusyService } from '../../shared/services/busy.service';
import { StatusService } from '../../shared/services/status.service';
import { OrgCategoryService } from '../../shared/services/org-category.service';
import { SelectItem } from 'primeng/components/common/api';
import { OrganizationService } from '../../shared/services/organization.service';
import { LazyLoadEvent } from 'primeng/components/common/lazyloadevent';
import { HeadendService } from '../../shared/services/headend.service';
import { Howl } from 'howler';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../shared/services/auth.service';
import { User } from '../../shared/models/User';

@Component({
  selector: 'app-organizations-list',
  templateUrl: './organizations-list.component.html',
  styleUrls: ['./organizations-list.component.scss']
})
export class OrganizationsListComponent implements OnInit {
  regions: any[];
  statuses: any[];
  orgCategories: any[];
  headEnds: any[];
  filter: any = {
    dayFlg: 2,
    statusID: null,
    categoryID: null,
    soundID: null,
    webOnlyFlg: null,
    zoneID: null,
    alpha: '',
    regionID: null
  };
  paginator: any = {
    perPage: 10,
    skip: 0,
    totals: 0
  };
  organizations: any[];
  alphas = '';
  deleteActionModel: any = {
    deleteConfirmationShow: false,
    organizationName: '',
    orgId: -1
  };
  busy = false;
  currentAudio: any;
  user: User;
  regionName = '';
  filterCacheKey: string;
  paginatorCacheKey: string;
  addExtraWidthForStatusColumn = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private busyService: BusyService,
    private regionService: RegionService,
    private statusService: StatusService,
    private orgCategoryService: OrgCategoryService,
    private organizationService: OrganizationService,
    private headendService: HeadendService,
    private authService: AuthService
  ) {
    this.user = AuthService.currentUser();

    if (!this.user.isInRole(['sysadmin', 'superuser'])) {
      this.router.navigate(['/orgedit/' + this.user.organizationId]);
    }

    this.filterCacheKey =
      'org-list-filter-' + this.user.organizationId.toString();
    this.paginatorCacheKey =
      'org-list-paginator-' + this.user.organizationId.toString();

    this.restoreTableState();

    this.busyService.initHandler('orgListCmp', busy => {
      this.busy = busy;
    });
  }

  ngOnInit() {
    this.route.data.subscribe(data => {
      this.busyService
        .follow('orgListCmp', this.regionService.all())
        .subscribe(regions => {
          this.regions = regions;
          if (this.user.regionId) {
            this.regionName = this.regions.filter(
              item => item.regionId === this.user.regionId
            )[0].regionName;
            this.loadHeadend();
          }
        });
      this.busyService
        .follow('orgListCmp', this.orgCategoryService.all())
        .subscribe(categories => {
          this.orgCategories = categories;
        });
      this.busyService
        .follow('orgListCmp', this.statusService.filt())
        .subscribe(statuses => {
          this.statuses = statuses;
          this.filter.status = this.statuses[0].statusID;
        });
    });
  }

  filterData() {
    if (this.user.regionId > 0 && this.user.regionId !== this.filter.regionID) {
      this.filter.regionID = this.user.regionId;
    }

    this.saveTableState();

    this.busyService
      .follow(
      'orgListCmp',
      this.organizationService.filter(
        this.filter,
        this.paginator.skip,
        this.paginator.perPage
      )
      )
      .subscribe(orgs => {
        this.organizations = orgs.rows;
        if (this.organizations) {
          this.addExtraWidthForStatusColumn = this.addExtraWidthForStatus(this.organizations);
        }
        this.paginator.totals = orgs.count;
        if (orgs.targets.alphas) {

          if (orgs.targets.alphas.search(new RegExp(/[0-9]/g)) > -1) {
            orgs.targets.alphas = '#' + orgs.targets.alphas;
          }

          this.paginator.alphas = orgs.targets.alphas
            .replace(' ', '')
            .replace(/[0-9]/g, '')
            .split('');
        } else {
          this.paginator.alphas = null;
        }
      });
  }

  search() {
    this.paginator.skip = 0;
    this.filter.alpha = '';
    this.filterData();
  }

  clearFilter() {
    this.filter = {
      dayFlg: 2,
      statusID: null,
      categoryID: null,
      soundID: null,
      webOnlyFlg: null,
      zoneID: null,
      alpha: '',
      regionID: null
    };
    this.paginator = {
      perPage: 10,
      skip: 0,
      rowsPerPageOptions: [10, 20, 50, 100],
      totals: 0
    };
    if (this.user.isInRole(['superuser'])) {
      this.headEnds = null;
    }
    this.filterData();
  }

  loadLazy($event: LazyLoadEvent) {
    this.paginator.skip = $event.first;
    this.paginator.perPage = $event.rows;
    this.filterData();
  }

  loadHeadend() {
    if (!this.filter.regionID) {
      this.headEnds = null;
      return;
    }

    this.busyService
      .follow(
      'orgListCmp',
      this.headendService.getByRegionId(this.filter.regionID)
      )
      .subscribe(r => {
        this.headEnds = r;
      });
  }

  selectAlpha(letter) {
    this.paginator.skip = 0;
    this.filter.alpha = letter;
    this.filterData();
  }

  showDeleteDialog(orgId?: number, organizationName?: string) {
    this.deleteActionModel.deleteConfirmationShow = !this.deleteActionModel
      .deleteConfirmationShow;
    this.deleteActionModel.orgId = orgId;
    this.deleteActionModel.organizationName = organizationName;
  }

  deleteOrg() {
    this.busyService
      .follow(
      'orgListCmp',
      this.organizationService.delete(this.deleteActionModel.orgId)
      )
      .subscribe(res => {
        this.showDeleteDialog();
        this.filterData();
      });
  }

  editOrganization(org) {
    this.router.navigate(['orgedit/' + org.orgId]);
  }

  editOrganizationStatus(org) {
    this.router.navigate(['orgstatus/' + org.orgId]);
  }

  getSoundFileApi(org) {
    const soundFilepath =
      environment.api.baseUrl +
      environment.api.organizations.playSound +
      '?access_token=' +
      encodeURIComponent(this.user.token);
    return soundFilepath;
  }

  getToPaginatorLabel() {
    if (this.paginator.perPage > this.paginator.totals) {
      return this.paginator.totals;
    } else {
      if (
        this.paginator.perPage + this.paginator.skip >
        this.paginator.totals
      ) {
        return this.paginator.totals;
      } else {
        return this.paginator.skip + this.paginator.perPage;
      }
    }
  }

  private saveTableState() {
    localStorage.setItem(this.filterCacheKey, JSON.stringify(this.filter));
    localStorage.setItem(
      this.paginatorCacheKey,
      JSON.stringify(this.paginator)
    );
  }

  private restoreTableState() {
    if (localStorage.getItem(this.filterCacheKey)) {
      this.filter = JSON.parse(localStorage.getItem(this.filterCacheKey));
    }
    if (localStorage.getItem(this.paginatorCacheKey)) {
      this.paginator = JSON.parse(localStorage.getItem(this.paginatorCacheKey));
    }
  }

  private addExtraWidthForStatus(rows) {
    return rows.filter((r) => !!r.statusDescription).length > 0;
  }
}



// WEBPACK FOOTER //
// E:/proj/news12/zb-n12/scadmin/client/src/app/authorized/organizations-list/organizations-list.component.ts